import { useLogsAndViewsPageContext } from '@monorepo/logs/src/contexts/LogsAndViewsPageContext';
import { EmptyState } from '@monorepo/shared/componentsV2/EmptyState';
import { ErrorBoundary } from '@monorepo/shared/componentsV2/ErrorBoundary';
import { Loading } from '@monorepo/shared/componentsV2/Loading';
import { Main } from '@monorepo/shared/componentsV2/Main';
import { viewsProductGuideUrl } from '@monorepo/shared/externalLinks/productGuide';
import { useSearchParamState } from '@monorepo/shared/hooks/navigation/useSearchParamState';
import { focusOutline } from '@monorepo/shared/styles/focus';
import {
  fillHeightAndScrollable,
  page,
  verticalLayout,
} from '@monorepo/shared/styles/layout';
import { header4 } from '@monorepo/shared/styles/text';
import { EdpViewResponse } from 'mapistry-shared';
import React, { useLayoutEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useQueryResults } from '@monorepo/shared/hooks/useQueryResults';
import { QueryResultsTable } from '@monorepo/shared/componentsV2/QueryResultsTable';
import { EditViewInfoModalProvider } from '../../../contexts/EditViewInfoModalContext';
import {
  QueryStepsProvider,
  useQuerySteps,
} from '../../../contexts/QueryStepsContext';
import { useView } from '../../../hooks/useView';
import { EditViewHeader } from './EditViewHeader';
import { QueryStepsAccordionGroup } from './QueryStepsAccordionGroup';
import { QueryStepToolbar } from './QueryStepToolbar';

const Page = styled(Main)`
  ${verticalLayout}
  ${page}
`;

const Content = styled.div`
  ${fillHeightAndScrollable}

  &:focus-visible {
    ${focusOutline({ $outlineOffset: '-2px' })}
  }
`;

const StyledQuerySteps = styled(QueryStepsAccordionGroup)`
  padding-bottom: 3rem;
`;

const StyledEmptyState = styled(EmptyState)`
  margin-bottom: calc(75px - 1rem);
`;

const Header2 = styled.h2`
  ${header4}
  padding: 1rem 1.5rem;
  margin: 0;
`;

const StyledQueryResultsTable = styled(QueryResultsTable)`
  height: auto;
`;

const buttonConfig = {
  label: 'Learn about Reports',
  onClick: () => window.open(viewsProductGuideUrl, '_blank'),
};

interface EditViewPageContentProps {
  organizationId: string;
  originId: string;
  projectId: string;
  view: EdpViewResponse | undefined;
}

function EditViewPageContent({
  organizationId,
  originId,
  projectId,
  view,
}: EditViewPageContentProps) {
  const { getInProgressQuerySteps, validQuerySteps } = useQuerySteps();

  const updatedView = useMemo(
    () => ({ ...view, querySteps: validQuerySteps }),
    [validQuerySteps, view],
  );

  const {
    isIdle: queryIsIdle,
    isLoading: queryIsLoading,
    isFetching: queryIsFetching,
    queryResult,
  } = useQueryResults({
    originId,
    organizationId,
    projectId,
    query: { steps: validQuerySteps || [] },
    config: { enabled: !!validQuerySteps },
  });

  const rowCountLabel = useMemo(() => {
    if (!queryResult) return '';
    const { fullCount, perPage } = queryResult.results.pagination;
    return fullCount < perPage
      ? `(showing all ${fullCount} rows)`
      : `(showing ${perPage} out of ${fullCount} rows)`;
  }, [queryResult]);

  return (
    <ErrorBoundary>
      <EditViewInfoModalProvider>
        <Page>
          <EditViewHeader view={updatedView} originId={originId} />
          <Content>
            {getInProgressQuerySteps().length ? (
              <>
                <StyledQuerySteps />
                <QueryStepToolbar />
              </>
            ) : (
              <>
                <QueryStepToolbar />
                <StyledEmptyState
                  actionButtonConfigs={buttonConfig}
                  primaryText="Add a query step above to begin transforming your data."
                  secondaryText="Adding query steps will allow you to save relevant reports of your data so you can more easily understand the health of your requirements."
                />
              </>
            )}
            <Header2>Data Preview {rowCountLabel}</Header2>
            <StyledQueryResultsTable
              isLoading={queryIsFetching || queryIsLoading || queryIsIdle}
              originId={originId}
              organizationId={organizationId}
              projectId={projectId}
              queryResult={queryResult}
              ariaName="Current Report"
            />
          </Content>
        </Page>
      </EditViewInfoModalProvider>
    </ErrorBoundary>
  );
}

export function EditViewPage() {
  const { organizationId, projectId, viewId } = useParams();
  const [searchParamsOriginId] = useSearchParamState('originId');

  if (!organizationId || !projectId) {
    throw new Error(
      "EditViewPage was rendered with a url that doesn't have projectId or organizationId",
    );
  }

  const { hideHeader, showHeader } = useLogsAndViewsPageContext();
  useLayoutEffect(() => {
    hideHeader();
    return showHeader;
  }, [hideHeader, showHeader]);

  const { view, isLoading } = useView({ organizationId, projectId, viewId });

  if (isLoading) {
    return <Loading />;
  }

  const originId = view?.originId || searchParamsOriginId;
  if (!originId) {
    throw new Error(
      'Either we need to be editing an existing view, or the originId needs to be defined in the url.',
    );
  }

  return (
    <QueryStepsProvider
      originId={originId}
      organizationId={organizationId}
      projectId={projectId}
      view={view}
    >
      <EditViewPageContent
        view={view}
        organizationId={organizationId}
        projectId={projectId}
        originId={originId}
      />
    </QueryStepsProvider>
  );
}
